import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService, FxRateSnapshotRecord } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-control-center-fx-rates',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule, AinDatePipe],
  template: `
    <ain-page [title]="t('finance.multiCurrency.fxRates.title')" [description]="t('finance.multiCurrency.fxRates.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('common.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('catalog.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelectedDetail()"></button>
            <button pButton type="button" [label]="t('finance.multiCurrency.actions.approve')" severity="secondary" [disabled]="selectedRows().length === 0" (click)="approveSelected()"></button>
            <button pButton type="button" [label]="t('finance.multiCurrency.actions.reject')" severity="secondary" [disabled]="selectedRows().length === 0" (click)="rejectSelected()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>

          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('catalog.filter.hide') : t('catalog.filter.show')" severity="secondary" (click)="toggleFilters()"></button>
          </div>

          @if (showFilters()) {
            <div class="filters">
              <label>
                <span>{{ t('finance.multiCurrency.fxRates.effectiveDate') }}</span>
                <input pInputText type="date" [(ngModel)]="filters.effectiveDateUtc" (ngModelChange)="applyFilters(true)" />
              </label>
              <label>
                <span>{{ t('finance.multiCurrency.fxRates.fromCurrency') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.fromCurrencyCode" (ngModelChange)="applyFilters(true)" />
              </label>
              <label>
                <span>{{ t('finance.multiCurrency.fxRates.toCurrency') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.toCurrencyCode" (ngModelChange)="applyFilters(true)" />
              </label>
              <label>
                <span>{{ t('columns.status') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.status" (ngModelChange)="applyFilters(true)" />
              </label>
            </div>
          }

          @if (!rows().length) {
            <ain-state type="empty" [title]="t('common.noRecords')" />
          } @else {
            <p-table
              [value]="rows()"
              dataKey="id"
              [paginator]="true"
              [rows]="15"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('finance.multiCurrency.fxRates.effectiveDate') }}</th>
                  <th>{{ t('finance.multiCurrency.fxRates.fromCurrency') }}</th>
                  <th>{{ t('finance.multiCurrency.fxRates.toCurrency') }}</th>
                  <th>{{ t('finance.multiCurrency.fxRates.rate') }}</th>
                  <th>{{ t('columns.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td>
                    <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.effectiveDateUtc | ainDate }}</button>
                  </td>
                  <td>{{ row.fromCurrencyCode }}</td>
                  <td>{{ row.toCurrencyCode }}</td>
                  <td>{{ row.rate }}</td>
                  <td>{{ row.approvalStatus }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; justify-content: flex-start; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .filters label { display: grid; gap: 6px; }
    .filters span { font-size: 12px; color: #475569; font-weight: 600; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class FinanceControlCenterFxRatesComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly showFilters = signal(true);
  readonly sourceRows = signal<FxRateSnapshotRecord[]>([]);
  readonly rows = signal<FxRateSnapshotRecord[]>([]);
  readonly selectedRows = signal<FxRateSnapshotRecord[]>([]);

  readonly filters = {
    effectiveDateUtc: '',
    fromCurrencyCode: '',
    toCurrencyCode: '',
    status: ''
  };

  ngOnInit(): void {
    this.filters.effectiveDateUtc = this.route.snapshot.queryParamMap.get('effectiveDateUtc') ?? '';
    this.filters.fromCurrencyCode = this.route.snapshot.queryParamMap.get('fromCurrencyCode') ?? '';
    this.filters.toCurrencyCode = this.route.snapshot.queryParamMap.get('toCurrencyCode') ?? '';
    this.filters.status = this.route.snapshot.queryParamMap.get('status') ?? '';
    this.showFilters.set((this.route.snapshot.queryParamMap.get('showFilters') ?? '1') !== '0');
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listFxRates().subscribe({
      next: rows => {
        this.sourceRows.set(rows);
        this.applyFilters(false);
        this.loading.set(false);
      },
      error: () => {
        this.sourceRows.set([]);
        this.rows.set([]);
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  toggleFilters(): void {
    this.showFilters.set(!this.showFilters());
    this.syncQueryParams();
  }

  applyFilters(syncQueryParams: boolean): void {
    const effectiveDateUtc = this.filters.effectiveDateUtc.trim();
    const fromCurrencyCode = this.filters.fromCurrencyCode.trim().toLowerCase();
    const toCurrencyCode = this.filters.toCurrencyCode.trim().toLowerCase();
    const status = this.filters.status.trim().toLowerCase();

    const filtered = this.sourceRows().filter(item => {
      const day = item.effectiveDateUtc?.slice(0, 10) ?? '';
      return (!effectiveDateUtc || day === effectiveDateUtc)
        && (!fromCurrencyCode || item.fromCurrencyCode.toLowerCase().includes(fromCurrencyCode))
        && (!toCurrencyCode || item.toCurrencyCode.toLowerCase().includes(toCurrencyCode))
        && (!status || item.approvalStatus.toLowerCase().includes(status));
    });

    this.rows.set(filtered);
    const selectedIds = new Set(this.selectedRows().map(item => item.id));
    this.selectedRows.set(filtered.filter(item => selectedIds.has(item.id)));
    if (syncQueryParams) {
      this.syncQueryParams();
    }
  }

  onSelectionChange(value: FxRateSnapshotRecord[]): void {
    this.selectedRows.set(value ?? []);
  }

  openCreate(): void {
    void this.router.navigate(['/financial/control-center/fx-rates', 'new'], { queryParams: this.currentQueryParams() });
  }

  openDetail(id: string): void {
    void this.router.navigate(['/financial/control-center/fx-rates', id], { queryParams: this.currentQueryParams() });
  }

  openSelectedDetail(): void {
    const selected = this.selectedRows();
    if (selected.length === 1) {
      this.openDetail(selected[0].id);
    }
  }

  approveSelected(): void {
    const selected = this.selectedRows().filter(item => item.approvalStatus.toLowerCase() !== 'approved');
    if (!selected.length) {
      return;
    }

    let completed = 0;
    for (const row of selected) {
      this.api.approveFxRate(row.id).subscribe({
        next: () => {
          completed += 1;
          if (completed === selected.length) {
            this.load();
          }
        },
        error: () => this.error.set(this.t('common.saveFailed'))
      });
    }
  }

  rejectSelected(): void {
    const selected = this.selectedRows().filter(item => item.approvalStatus.toLowerCase() !== 'rejected');
    if (!selected.length) {
      return;
    }

    let completed = 0;
    for (const row of selected) {
      this.api.rejectFxRate(row.id).subscribe({
        next: () => {
          completed += 1;
          if (completed === selected.length) {
            this.load();
          }
        },
        error: () => this.error.set(this.t('common.saveFailed'))
      });
    }
  }

  private syncQueryParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.currentQueryParams(),
      replaceUrl: true
    });
  }

  private currentQueryParams(): Record<string, string> {
    return {
      effectiveDateUtc: this.filters.effectiveDateUtc.trim(),
      fromCurrencyCode: this.filters.fromCurrencyCode.trim(),
      toCurrencyCode: this.filters.toCurrencyCode.trim(),
      status: this.filters.status.trim(),
      showFilters: this.showFilters() ? '1' : '0'
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
